/**
 * Wire models for the Copilot Connectors service.
 *
 * Transcribed from `copilot-connectors/docs/integrators.md`, cross-checked against
 * the desktop client's `src-tauri/src/connectors/models.rs`.
 *
 * Two endpoints use two different vocabularies. `GET /plugins` uses camelCase
 * field names with snake_case status values. The managed-connection endpoints
 * use snake_case field names with PascalCase status values. They are not
 * interchangeable.
 *
 * The catalog grows additive fields over time, so every status union keeps a
 * `string` tail and every optional field is treated as possibly absent. A
 * connector added next month should render as unknown rather than break the
 * page.
 *
 * Status is polled, never pushed. The service does not notify clients when a
 * connection changes, and completing the consent callback does not notify the
 * client that started it. Re-poll `GET /plugins` after a connect flow and
 * whenever the user opens this surface.
 */

/** Connection state from `GET /plugins`. The `string` tail is the tolerant fallback. */
export type ConnectionStatus = 'not_connected' | 'pending' | 'connected' | 'error' | (string & {})

export interface PluginMetadata {
  displayName?: string
  capabilities?: string[]
  isExportSupported?: boolean
}

export interface PluginConnection {
  status?: ConnectionStatus
  /** RFC 9728 protected-resource metadata URL. Empty until an issuer is configured. */
  protectedResourceMetadataUrl?: string
  scopes?: string[]
}

export interface PluginMcpServer {
  type?: string
  url?: string
}

export interface PluginMcpServers {
  mcpServers?: Record<string, PluginMcpServer>
}

export interface PluginAuthor {
  name?: string
  email?: string
  url?: string
}

/**
 * One entry in the connector catalog, annotated with the caller's connection status.
 *
 * `mcpServers` is service-controlled and opaque. Read the advertised URL as-is and
 * re-read it rather than caching it; GitHub may change host, path, or routing.
 */
export interface Plugin {
  name: string
  version?: string
  description?: string
  author?: PluginAuthor
  homepage?: string
  repository?: string
  license?: string
  logo?: string
  keywords?: string[]
  commands?: unknown[]
  agents?: unknown[]
  skills?: unknown[]
  metadata?: PluginMetadata
  connection?: PluginConnection
  mcpServers?: PluginMcpServers
}

/** Response body of `GET /plugins`. */
export interface PluginsResponse {
  plugins?: Plugin[]
}

/**
 * Bounded `client_source` accepted by `PUT .../connection`. Missing and blank values
 * default to `UNKNOWN`; any other value is rejected. This is metadata that selects
 * server-owned completion UX, never a client-supplied return destination.
 */
export type ClientSource =
  | 'COPILOT_CLI'
  | 'COPILOT_SDK'
  | 'COPILOT_APP'
  | 'VS_CODE'
  | 'GITHUB_DOTCOM'
  | 'DOTCOM_CHAT'
  | 'UNKNOWN'

/** Connection state from the managed-connection endpoints. */
export type LifecycleStatus = 'Connected' | 'Unauthenticated' | 'Unknown' | (string & {})

export interface LifecycleStatusError {
  code?: string
  message?: string
}

export interface LifecycleStatusEntry {
  target?: string
  status?: string
  error?: LifecycleStatusError | null
}

/**
 * Response body of `PUT`/`GET /connectors/managed/{name}/connection`.
 *
 * `consentLink` is present only while consent is pending. It must be opened
 * client side and never followed by a server.
 */
export interface ConnectorConnectionResponse {
  status?: LifecycleStatus
  provisioning_state?: string
  statuses?: LifecycleStatusEntry[]
  consent_link?: string | null
}

/** Key carrying the GitHub error extension inside a JSON-RPC `error.data`. */
export const CONNECTOR_ERROR_EXTENSION_KEY = 'com.github/copilot-connector-errors'

/**
 * JSON-RPC error codes returned by the MCP proxy. Recovery is driven by the
 * extension `reason` and `action`, not by the accompanying HTTP status.
 */
export const ConnectorErrorCode = {
  connectionBroken: 1001,
  connectionMissing: 1006,
  connectionUnsettled: 1007,
} as const

export interface ConnectorErrorExtension {
  reason?: 'connection_broken' | 'connection_missing' | 'connection_unsettled' | (string & {})
  action?: 'reconnect' | 'connect' | 'retry' | (string & {})
  retryable?: boolean
  documentation?: string
}

/** Normalizes the catalog status, mapping anything unrecognized to `unknown`. */
export function normalizeConnectionStatus(status: ConnectionStatus | undefined): NormalizedStatus {
  switch (status) {
    case 'not_connected':
      return 'not_connected'
    case 'pending':
      return 'pending'
    case 'connected':
      return 'connected'
    case 'error':
      return 'error'
    default:
      return 'unknown'
  }
}

export type NormalizedStatus = 'not_connected' | 'pending' | 'connected' | 'error' | 'unknown'

/** Falls back to the connector's service name when the catalog omits a display name. */
export function pluginDisplayName(plugin: Plugin): string {
  return plugin.metadata?.displayName?.trim() || plugin.name
}

export function isConnected(plugin: Plugin): boolean {
  return normalizeConnectionStatus(plugin.connection?.status) === 'connected'
}

/** Case-insensitive match on the name and description shown in a row. */
export function filterPlugins(plugins: Plugin[], query: string): Plugin[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return plugins

  return plugins.filter(plugin => {
    const haystack = [pluginDisplayName(plugin), plugin.name, plugin.description ?? '']
    return haystack.some(value => value.toLowerCase().includes(needle))
  })
}
